import type { Page } from "@/types/pagination";
import type {
  AvailableCreditResponse,
  PatientCredit,
  PatientCreditCreate,
  PatientCreditTransaction,
  PatientCreditUpdate,
} from "@/types/patient-credit";

import { requestData } from "@/api/api-config";

export async function getPatientCredits(
  patientId: string,
  params?: {
    page?: number;
    perPage?: number;
    includeExpired?: boolean;
    includeUsed?: boolean;
  },
): Promise<Page<PatientCredit> | null> {
  return await requestData<Page<PatientCredit>>(
    `user/patient/${patientId}/credit`,
    "get",
    {
      searchParams: {
        page: params?.page,
        per_page: params?.perPage,
        include_expired: params?.includeExpired,
        include_used: params?.includeUsed,
      },
    },
  );
}

export async function getAvailableCredit(
  patientId: string,
): Promise<AvailableCreditResponse | null> {
  return await requestData<AvailableCreditResponse>(
    `user/patient/${patientId}/credit/available`,
    "get",
  );
}

export async function getPatientCreditTransactions(
  patientId: string,
  creditId: string,
): Promise<PatientCreditTransaction[] | null> {
  return await requestData<PatientCreditTransaction[]>(
    `user/patient/${patientId}/credit/${creditId}/transactions`,
    "get",
  );
}

export async function createPatientCredit(
  patientId: string,
  data: PatientCreditCreate,
): Promise<PatientCredit | null> {
  // Remove patientId from data since it's in the URL path
  const { patientId: _, ...bodyData } = data;
  return await requestData<PatientCredit>(
    `user/patient/${patientId}/credit`,
    "post",
    {
      json: bodyData,
    },
    // Throw so callers keep the form open and show why the server refused it.
    false,
  );
}

export async function updatePatientCredit(
  patientId: string,
  creditId: string,
  data: PatientCreditUpdate,
): Promise<PatientCredit | null> {
  return await requestData<PatientCredit>(
    `user/patient/${patientId}/credit/${creditId}`,
    "patch",
    {
      json: data,
    },
    false,
  );
}

export async function deletePatientCredit(
  patientId: string,
  creditId: string,
): Promise<{ message: string } | null> {
  return await requestData<{ message: string }>(
    `user/patient/${patientId}/credit/${creditId}`,
    "delete",
  );
}
